// Lib
import { FC, useEffect, useState } from "react";
// Api
import {
  useLazyGetCashPaymentsSettingsQuery,
  useUpdateCashPaymentsSettingsMutation,
} from "rtkQuery/query/cashPaymentsSettingsAPI";
import {
  useLazyGetSettingsQuery,
  useUpdateSettingsMutation,
} from "rtkQuery/query/settingsAPI";
// Hooks
import { useNotification, usePermissions } from "hooks";
// Helpers
import { getConfirmModalTitle, getWarningModalTitle } from "../../helpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { PersonIcon } from "icons";
// Components
import { ConfirmDialog, WarningDialog } from "components/Modals";
import SettingsItem from "../SettingsItem";
import { LocationsInput, Switch, Input } from "../Inputs";
// Styled
import { ContentBox, SubContentBox } from "styled/Box";
import { Typography } from "styled/Typography";
import { Title } from "./styled";

import {
  Payment,
  ConfirmCashPaymentModalType,
  initialLoadingState,
  initialState,
  LoadingStateValues,
  notificationMessage,
  StateValues,
} from "./config";
import { getSettingsName } from "pages/Settings/helpers";

export const PaymentSettings: FC = () => {
  const { openNotification } = useNotification();

  const { canPaymentSettingsUpdate } = usePermissions();

  const [get] = useLazyGetCashPaymentsSettingsQuery();
  const [update] = useUpdateCashPaymentsSettingsMutation();
  const [fetchSettings] = useLazyGetSettingsQuery();
  const [updateSettings] = useUpdateSettingsMutation();

  const [settings, setSettings] = useState<StateValues>(initialState);

  const [isLoading, setIsLoading] =
    useState<LoadingStateValues>(initialLoadingState);

  const [confirmModal, setConfirmModal] = useState<
    ConfirmCashPaymentModalType | false
  >(false);

  const [warningModal, setWarningModal] = useState<
    ConfirmCashPaymentModalType | false
  >(false);

  const [areasModal, setAreasModal] = useState(false);

  const getMinimunSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [Payment.MinimumOrderValue]: true,
        [Payment.MinimumTopUpValue]: true,
      }));

      const { minimumOrderValue, minimumTopUpValue, isDeliveryFeeAllowed } =
        await fetchSettings().unwrap();

      setSettings(prev => ({
        ...prev,
        minimumOrderValue,
        minimumTopUpValue,
        isDeliveryFeeAllowed,
      }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        [Payment.MinimumOrderValue]: false,
        [Payment.MinimumTopUpValue]: false,
      }));
    }
  };

  const getSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        [Payment.IsCashPayment]: true,
        [Payment.DeliveryFeeFiatCents]: true,
        [Payment.DeliveryFeeFmcCents]: true,
        [Payment.Discount]: true,
        [Payment.MinimumOrder]: true,
        [Payment.IsCashPaymentForColdKitchen]: true,
      }));

      const data = await get().unwrap();

      setSettings(prev => ({
        ...prev,
        ...data,
      }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        [Payment.IsCashPayment]: false,
        [Payment.DeliveryFeeFiatCents]: false,
        [Payment.DeliveryFeeFmcCents]: false,
        [Payment.Discount]: false,
        [Payment.MinimumOrder]: false,
        [Payment.IsCashPaymentForColdKitchen]: false,
      }));
    }
  };

  useEffect(() => {
    getSettings();
    getMinimunSettings();
  }, []);

  const handleUpdateSettings = async (
    key: Payment,
    value: number | boolean,
  ) => {
    const isValueNumber = typeof value === "number";
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      const payload = {
        [Payment.MinimumOrderValue]: settings[Payment.MinimumOrderValue],
        [Payment.MinimumTopUpValue]: settings[Payment.MinimumTopUpValue],
        [Payment.isDeliveryFeeAllowed]: settings[Payment.isDeliveryFeeAllowed],
        [key]: value,
      };

      if (isValueNumber) {
        await updateSettings(payload).unwrap();

        setSettings(prev => ({ ...prev, [key]: value }));
      } else {
        await updateSettings(payload).unwrap();
      }

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      if (!isValueNumber) {
        setSettings(prev => ({ ...prev, [key]: !value }));
      }

      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));

      if (confirmModal) {
        setConfirmModal(false);
      }
    }
  };

  const handleUpdateCashPaymentSettings = async (
    key: Payment,
    value: boolean | number | string[],
  ) => {
    const isValueNumber = typeof value === "number";
    const isArray = Array.isArray(value);

    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      const payload = {
        deliveryFeeFiatCents: settings[Payment.DeliveryFeeFiatCents],
        discount: settings[Payment.Discount],
        isCashPayment: settings[Payment.IsCashPayment],
        minimumOrder: settings[Payment.MinimumOrder],
        deliveryFeeFmcCents: settings[Payment.DeliveryFeeFmcCents],
        availableLocations: settings[Payment.AvailableLocations],
        isCashPaymentForColdKitchen:
          settings[Payment.IsCashPaymentForColdKitchen],
        [key]: value,
      };

      if (isValueNumber || isArray) {
        await update(payload).unwrap();

        setSettings(prev => ({ ...prev, [key]: value }));

        if (areasModal) {
          setAreasModal(false);
        }
      } else {
        setSettings(prev => ({ ...prev, [key]: value }));

        await update(payload).unwrap();
      }

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      if (!isValueNumber || !isArray) {
        setSettings(prev => ({ ...prev, [key]: !value }));
      }

      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));

      if (confirmModal) {
        setConfirmModal(false);
      }
    }
  };

  const handleUpdate = (key: Payment, value: StateValues[Payment]) => {
    const isCashPaymentConfig = key === Payment.IsCashPayment && value;

    if (isCashPaymentConfig && settings[Payment.isDeliveryFeeAllowed]) {
      setSettings(prev => ({ ...prev, [key]: value as boolean }));
      setWarningModal({ key, value: value as boolean });
      return;
    }

    const isDeliveryFeeConfig = key === Payment.isDeliveryFeeAllowed && value;

    if (isDeliveryFeeConfig && settings[Payment.IsCashPayment]) {
      setSettings(prev => ({ ...prev, [key]: value as boolean }));
      setWarningModal({ key, value: value as boolean });
      return;
    }

    switch (key) {
      case Payment.Discount:
      case Payment.DeliveryFeeFiatCents:
      case Payment.MinimumOrder:
      case Payment.DeliveryFeeFmcCents:
      case Payment.AvailableLocations:
        handleUpdateCashPaymentSettings(
          key,
          value as boolean | number | string[],
        );
        return;

      case Payment.IsCashPayment:
        setSettings(prev => ({ ...prev, [key]: value as boolean }));
        setConfirmModal({ key, value: value as boolean });
        return;
      case Payment.isDeliveryFeeAllowed:
        setSettings(prev => ({ ...prev, [key]: value as boolean }));
        setConfirmModal({ key, value: value as boolean });
        return;
      case Payment.IsCashPaymentForColdKitchen:
        setSettings(prev => ({ ...prev, [key]: value as boolean }));
        setConfirmModal({ key, value: value as boolean });
        return;

      default:
        break;
    }
  };

  const onConfirm = () => {
    if (!confirmModal) return;

    switch (confirmModal.key) {
      case Payment.IsCashPayment:
      case Payment.IsCashPaymentForColdKitchen:
        handleUpdateCashPaymentSettings(
          confirmModal.key,
          confirmModal.value as boolean,
        );
        return;
      case Payment.isDeliveryFeeAllowed:
        handleUpdateSettings(confirmModal.key, confirmModal.value as boolean);
        return;

      default:
        break;
    }
  };

  const onCancel = () => {
    if (!confirmModal) return;

    setSettings(prev => ({ ...prev, [confirmModal.key]: !confirmModal.value }));

    setConfirmModal(false);
  };

  const onWarningCancel = () => {
    if (!warningModal) return;

    setSettings(prev => ({ ...prev, [warningModal.key]: !warningModal.value }));

    setWarningModal(false);
  };

  const handleAreasModalOpen = () => {
    setAreasModal(true);
  };
  const handleAreasModalClose = () => {
    setAreasModal(false);
  };

  return (
    <>
      <ContentBox $column $gap={12} $padding="24px 12px 12px">
        <Title>Payments</Title>

        <SubContentBox $column $gap={12} $padding="12px 12px 0px">
          <Typography.H3>Cash</Typography.H3>

          <div>
            <SettingsItem title={getSettingsName(Payment.IsCashPayment)}>
              <Switch
                isLoading={isLoading[Payment.IsCashPayment]}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.IsCashPayment]}
                onChange={value => handleUpdate(Payment.IsCashPayment, value)}
              />
            </SettingsItem>

            <SettingsItem
              title={getSettingsName(Payment.IsCashPaymentForColdKitchen)}
              description="This setting enables Cold kitchen with cash payment feature only for the customers with the activated toggle 'Cold kitchen with cash payment'"
            >
              <Switch
                isLoading={isLoading[Payment.IsCashPaymentForColdKitchen]}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.IsCashPaymentForColdKitchen]}
                onChange={value =>
                  handleUpdate(Payment.IsCashPaymentForColdKitchen, value)
                }
              />
            </SettingsItem>

            <SettingsItem
              title={getSettingsName(Payment.AvailableLocations)}
              description="Set available kitchens for cash payment users"
            >
              <LocationsInput
                isLoading={isLoading[Payment.AvailableLocations]}
                isModalOpen={areasModal}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.AvailableLocations]}
                handleAreasModalOpen={handleAreasModalOpen}
                handleAreasModalClose={handleAreasModalClose}
                onSubmit={value =>
                  handleUpdate(Payment.AvailableLocations, value)
                }
              />
            </SettingsItem>

            <SettingsItem
              title={getSettingsName(Payment.MinimumOrder)}
              description="Set the minimum KWD required to proceed with the order for cash payment users"
            >
              <Input
                suffix="KWD"
                fixedDigitsPlaceholder={3}
                step={0.001}
                isLoading={isLoading[Payment.MinimumOrder]}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.MinimumOrder]}
                onSubmit={value => handleUpdate(Payment.MinimumOrder, value)}
              />
            </SettingsItem>

            <SettingsItem
              title={getSettingsName(Payment.DeliveryFeeFiatCents)}
              description="Set delivery fee for cash payment users"
            >
              <Input
                suffix="KWD"
                fixedDigitsPlaceholder={3}
                step={0.001}
                isLoading={isLoading[Payment.DeliveryFeeFiatCents]}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.DeliveryFeeFiatCents]}
                onSubmit={value =>
                  handleUpdate(Payment.DeliveryFeeFiatCents, value)
                }
              />
            </SettingsItem>
          </div>
        </SubContentBox>

        <SubContentBox $column $gap={12} $padding="12px 12px 0px">
          <Typography.H3>Coins</Typography.H3>

          <div>
            <SettingsItem
              title={getSettingsName(Payment.isDeliveryFeeAllowed)}
              description="For users with expired coin package"
            >
              <Switch
                isLoading={isLoading[Payment.isDeliveryFeeAllowed]}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.isDeliveryFeeAllowed]}
                onChange={value =>
                  handleUpdate(Payment.isDeliveryFeeAllowed, value)
                }
              />
            </SettingsItem>

            <SettingsItem
              title={getSettingsName(Payment.DeliveryFeeFmcCents)}
              description="Set delivery fee for users with expired coin package"
            >
              <Input
                suffix="FM Coins"
                fixedDigitsPlaceholder={3}
                step={0.001}
                isLoading={isLoading[Payment.DeliveryFeeFmcCents]}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.DeliveryFeeFmcCents]}
                onSubmit={value =>
                  handleUpdate(Payment.DeliveryFeeFmcCents, value)
                }
              />
            </SettingsItem>

            <SettingsItem
              title={getSettingsName(Payment.Discount)}
              description="Set discount percentages for users with active coin package"
            >
              <Input
                suffix="%"
                isLoading={isLoading[Payment.Discount]}
                canUpdate={canPaymentSettingsUpdate}
                data={settings[Payment.Discount]}
                onSubmit={value => handleUpdate(Payment.Discount, value)}
              />
            </SettingsItem>
          </div>
        </SubContentBox>

        <ConfirmDialog
          isLoading={confirmModal && isLoading[confirmModal.key]}
          open={!!confirmModal}
          Icon={PersonIcon}
          message={
            confirmModal &&
            getConfirmModalTitle({
              key: confirmModal.key,
              value: confirmModal.value,
            })
          }
          description="Are you sure to continue this action?"
          onCancel={onCancel}
          firstCTAButton={{
            title: "Accept",
            status: "danger",
            disabled: false,
            loading: confirmModal && isLoading[confirmModal.key],
            onClick: onConfirm,
          }}
        />

        <WarningDialog
          open={!!warningModal}
          Icon={PersonIcon}
          message={
            warningModal &&
            getWarningModalTitle({
              key: warningModal.key,
            })
          }
          onCancel={onWarningCancel}
        />
      </ContentBox>
    </>
  );
};
